import styles from './CircularTimer.module.scss';

export type CircularTimerStatus = 'waiting' | 'running' | 'finished';

interface CircularTimerProps {
  totalTime: number;
  remainingTime: number;
  status?: CircularTimerStatus;
  isInitializing?: boolean;
}

function CircularTimer({
  totalTime,
  remainingTime,
  status,
  isInitializing,
}: CircularTimerProps): JSX.Element {
  // Ensures values are not negative or invalid
  const safeRemainingTime = Math.max(0, remainingTime) || 0;
  const safeTotalTime = Math.max(0, totalTime);

  const elapsedTime = safeTotalTime - safeRemainingTime;

  // Calculates progress percentage
  const progressPercentage =
    safeTotalTime > 0 ? (elapsedTime / safeTotalTime) * 100 : 0;

  // Calculates the dashoffset for the SVG circle animation
  const circumference = 2 * Math.PI * 45; // Circumference for a radius of 45px
  const dashoffset = circumference - (progressPercentage / 100) * circumference;

  // Formats the remaining time to MM
  const remainingMinutes = Math.floor(safeRemainingTime / 60);

  return (
    <div
      role="timer"
      aria-live="polite"
      aria-label={`Tempo restante: ${remainingMinutes}`}
      data-testid="timer-container"
      className={`${styles.timerContainer} ${styles[status || '']}`}
    >
      <div className={styles.progressBarWrapper}>
        <svg className={styles.progressBarSvg} viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="45"
            className={styles.progressBarTrack}
            aria-hidden="true"
          />
          <circle
            cx="50"
            cy="50"
            r="45"
            data-testid="progress-bar-progress"
            className={`${styles.progressBarProgress} ${styles[status || '']}`}
            style={{
              strokeDashoffset: dashoffset,
            }}
            aria-hidden="true"
          />
        </svg>
        <div className={styles.timerText} aria-hidden="true">
          {isInitializing ? (
            <>
              <span>...</span>
            </>
          ) : (
            <>
              {remainingMinutes} min
              <br />
              <span className={styles.timerTextSub}>restantes</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CircularTimer;
